import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, ArrowLeft } from '@phosphor-icons/react';
import SEO from '../components/SEO';
import ListState from '../components/ListState';
import CategoryBadges from '../components/CategoryBadges';
import { logError } from '../lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCity = (c) => c?.split('-').map(w => w[0].toUpperCase() + w.slice(1).toLowerCase()).join(' ') || '';

const CityTournaments = () => {
  const { city } = useParams();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const cityName = formatCity(city);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/tournaments?city=${cityName}&status=approved`)
      .then(({ data }) => setTournaments(data))
      .catch(e => logError('Failed to load city tournaments:', e))
      .finally(() => setLoading(false));
  }, [city, cityName]);

  return (
    <>
      <SEO
        title={`Basketball Tournaments in ${cityName}`}
        description={`Find and register for basketball tournaments in ${cityName}, India. 1v1, 3v3, and 5v5 events. Powered by Beyond Ball India.`}
        path={`/tournaments/city/${city}`}
      />

      <div className="pt-24 pb-16 min-h-screen">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/tournaments" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#F97316] mb-6" data-testid="back-to-all-tournaments">
            <ArrowLeft size={16} weight="bold" /> All tournaments
          </Link>

          <div className="mb-10 animate-fade-up">
            <div className="text-xs font-semibold text-[#F97316] uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <MapPin size={14} weight="bold" /> {cityName}, India
            </div>
            <h1 className="text-3xl sm:text-5xl font-bold tracking-tight text-gray-900 mb-3" data-testid="city-title">
              Basketball tournaments in {cityName}
            </h1>
            <p className="text-gray-600 text-base sm:text-lg">Discover what's happening on courts in {cityName}.</p>
          </div>

          <ListState
            loading={loading}
            empty={tournaments.length === 0}
            skeleton={
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1,2,3].map(i => <div key={i} className="bg-white rounded-2xl border border-gray-200 h-64 skeleton"></div>)}
              </div>
            }
            emptyState={
              <div className="text-center py-20 bg-white border border-gray-200 rounded-2xl">
                <div className="text-5xl mb-3">🏀</div>
                <p className="text-gray-900 font-semibold mb-2">No tournaments in {cityName} yet</p>
                <Link to="/tournaments" className="text-[#F97316] hover:text-orange-700 font-semibold text-sm">Browse all tournaments</Link>
              </div>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tournaments.map((t, i) => (
                <Link key={t.tournament_id} to={`/tournaments/${t.tournament_id}`} className="card-lift bg-white rounded-2xl border border-gray-200 overflow-hidden animate-fade-up" style={{animationDelay: `${i*50}ms`}} data-testid={`tournament-card-${i}`}>
                  <div className="relative aspect-[16/10] bg-gradient-to-br from-orange-100 to-orange-50">
                    {t.banner_url ? <img src={t.banner_url} alt={t.name} className="w-full h-full object-cover" style={{ objectPosition: `${t.banner_focus_x ?? 50}% ${t.banner_focus_y ?? 50}%` }} loading="lazy" /> :
                      <div className="w-full h-full flex items-center justify-center"><span className="text-6xl font-bold text-orange-200">{t.format}</span></div>}
                    <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/95 text-xs font-semibold text-gray-900">{t.format}</div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-gray-900 mb-1 line-clamp-1">{t.name}</h3>
                    <CategoryBadges ages={t.age_categories || []} genders={t.gender_categories || []} className="mb-2" />
                    <div className="flex items-center gap-1.5 text-sm text-gray-500"><MapPin size={14} /> {t.venue}</div>
                  </div>
                </Link>
              ))}
            </div>
          </ListState>

          <div className="mt-14 bg-white border border-gray-200 rounded-2xl p-8">
            <h2 className="font-bold text-gray-900 text-xl mb-3">Basketball in {cityName}</h2>
            <p className="text-gray-600 leading-relaxed">{cityName} has a growing basketball community with regular 1v1, 3v3, and 5v5 tournaments. Beyond Ball India is your hub for every basketball event in {cityName} — whether you're a player looking for action or an organizer planning your next event.</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default CityTournaments;
